import { useEffect, useState } from 'react';
import { EChartsType } from 'echarts';
import {
  calculateDotPosition,
  getIconCoordinates,
  calculateConnectionLineStyle,
  IConnectionLineStyle,
} from '../utils/coordinateHelper';
import { IStudio } from '../const/studio';

type TConnectionLineItem = {
  studioName: string;
  style: IConnectionLineStyle;
};

interface IUseConnectionLineStylesParams {
  visible: boolean;
  hoveredStudioName: string | string[] | null;
  containerRef: React.RefObject<HTMLDivElement>;
  chartRef: React.RefObject<HTMLDivElement>;
  cardRefsMap: React.RefObject<Map<string, HTMLDivElement>>;
  chartInstance: EChartsType | undefined | null;
  studios?: IStudio[];
}

/**
 * 计算连接线样式的自定义 Hook
 * 支持单个或多个工作室的连接线计算
 */
const useConnectionLineStyles = ({
  visible,
  hoveredStudioName,
  containerRef,
  chartRef,
  cardRefsMap,
  chartInstance,
  studios = [],
}: IUseConnectionLineStylesParams): TConnectionLineItem[] => {
  const [connectionLineStyles, setConnectionLineStyles] = useState<TConnectionLineItem[]>([]);

  useEffect(() => {
    if (!hoveredStudioName || !visible) {
      setConnectionLineStyles([]);
      return;
    }

    const chartElement = chartRef.current;
    const containerElement = containerRef.current;
    const cardRefsMapValue = cardRefsMap.current;

    if (!containerElement || !chartElement) {
      return;
    }

    // 支持单个 studio 和多个 studios
    const studioNames = Array.isArray(hoveredStudioName)
      ? hoveredStudioName
      : [hoveredStudioName];

    // 构建 sub_studio -> parent studio 映射
    const subStudioToParentMap: Record<string, string> = {};
    studios.forEach(studio => {
      if (studio.sub_studio) {
        subStudioToParentMap[studio.sub_studio.name] = studio.name;
      }
    });

    const styles: TConnectionLineItem[] = [];

    studioNames.forEach(studioName => {
      try {
        // 检查是否是 sub_studio
        const parentStudioName = subStudioToParentMap[studioName];
        const isSubStudio = !!parentStudioName;

        // 获取卡片元素（sub_studio 使用父 studio 的卡片）
        const cardElement = cardRefsMapValue?.get(
          isSubStudio ? parentStudioName : studioName,
        );
        if (!cardElement) {
          return;
        }

        // 获取卡片在界面中的位置
        const positionAttr = cardElement.getAttribute('data-position') as
          | 'top'
          | 'left'
          | 'right'
          | 'bottom'
          | null;

        if (!positionAttr) {
          return;
        }

        // 计算 dot 位置（使用实际的 studioName 来找到对应的 dot 元素）
        const dotCoordinates = calculateDotPosition(
          containerElement,
          studioName,
        );
        const { x: dotX, y: dotY } = dotCoordinates;

        // 从地图获取对应工作室 icon 的坐标
        const iconCoordinates = getIconCoordinates(
          chartElement,
          studioName,
          containerElement,
          chartInstance,
        );

        if (!iconCoordinates) {
          return;
        }

        const { x: iconX, y: iconY, symbolSize } = iconCoordinates;

        // 计算连接线样式
        const style = calculateConnectionLineStyle(
          positionAttr,
          dotX,
          dotY,
          iconX,
          iconY,
          symbolSize,
        );

        styles.push({ studioName, style });
      } catch (error) {
        console.warn(
          `Failed to calculate connection line style for ${studioName}:`,
          error,
        );
      }
    });

    setConnectionLineStyles(styles);
  }, [
    hoveredStudioName,
    visible,
    containerRef,
    chartRef,
    cardRefsMap,
    chartInstance,
    studios,
  ]);

  return connectionLineStyles;
};

export type { TConnectionLineItem };
export default useConnectionLineStyles;
